import { Box, BoxProps, Center, CenterProps, Flex } from '~/design_system'

/**
 * @agent-preferred
 * @agent-gotcha no DS spinner exists in the DS yet; use this for loading states
 */
export const VndlySpinner = ({ color, size, ...props }: CenterProps & LoadingDotsProps) => (
  <Center h="300px" data-vndly-spinner data-testid="vndly-spinner" {...props}>
    <LoadingDots color={color} size={size} />
  </Center>
)

export const VndlyOverlaySpinner = ({ color = 'white', size, ...props }: BoxProps & LoadingDotsProps) => (
  <Box
    background="rgba(0, 0, 0, 0.48)"
    width="100%"
    height="100%"
    zIndex={9999}
    top={0}
    left={0}
    position="fixed"
    display="flex"
    justifyItems="center"
    justifyContent="center"
    overflow="hidden"
    flexDirection="column"
    {...props}>
    <VndlySpinner color={color} size={size} />
  </Box>
)

/**
 * Keyframes for the dots loading animation using Chakra UI style props.
 */
const loadingKeyframes = {
  '0%, 80%, 100%': { transform: 'scale(0)' },
  '40%': { transform: 'scale(1)' }
}

type LoadingDotsProps = {
  color?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
}
/**
 * A simple component that displays three horizontal dots, to be used when some data is loading.
 */
export default function LoadingDots(props: LoadingDotsProps) {
  const dotColor = props.color ?? 'brand.primary.base'
  const dotSize = {
    xs: 8,
    sm: 12,
    md: 16,
    lg: 24
  }[props.size ?? 'lg']
  const delays = [0, 160, 320]

  return (
    <Flex gap={`${dotSize / 4}px`}>
      {delays.map(delay => (
        <Box
          key={delay}
          bg={dotColor}
          w={`${dotSize}px`}
          h={`${dotSize}px`}
          borderRadius="full"
          transform="scale(0)"
          animation="loadingDot 1s ease-in-out infinite"
          sx={{
            '@keyframes loadingDot': loadingKeyframes,
            animationDelay: `${delay}ms`,
            fontSize: '0px',
            display: 'inline-block'
          }}
        />
      ))}
    </Flex>
  )
}
